import HomeFooter from '@/Home/HomeFooter.vue';
import HighlightedLocations from '@/Home/HighlightedLocations.vue';
import { ROUTE_LIST_NAMES } from '@/config/router/appRoutes';
import { AuthPopups } from '@/Authentication/authentication.const';
import TrustPublication from '@/components/TrustPublication.vue';
import { computed, defineComponent, onMounted, onUnmounted, watch, ref, getCurrentInstance } from '@vue/composition-api';
import useStructuredData from '@/shared/composables/useStructuredData';
import { SITE_URL } from '@/App.vue';
import PageFAQ from '@/components/PageFAQ.vue';
import { useGetHomePageContentQuery } from '@/generated-types/graphql.types';
import { useStoryblokBridge } from '@/shared/composables/useStoryblokBridge';
export default defineComponent({
    components: {
        PageFAQ,
        TrustPublication,
        HomeFooter,
        HighlightedLocations
    },
    setup(_, context) {
        const instance = getCurrentInstance();
        const { root } = context;
        const { result, loading, refetch } = useGetHomePageContentQuery({ enabled: true });
        const storyBase = ref(null);
        const story = computed({
            get: () => storyBase.value,
            set: (value) => (storyBase.value = value)
        });
        // The page order, for as long as the CMS story is empty. Storyblok has no
        // q4s_* blocks in the home story yet and get_home_page_cms_content returns
        // nothing on this environment, which is why the homepage was blank here.
        //
        // Once the CMS story carries these blocks its body wins and this array is
        // dead weight -- delete it then rather than editing it in two places.
        const DEFAULT_HOME_BODY = [
            { _uid: 'default-hero', component: 'q4s_hero' },
            { _uid: 'default-steps', component: 'q4s_how_it_works' },
            { _uid: 'default-promises', component: 'q4s_promises' },
            { _uid: 'default-vendor', component: 'q4s_vendor_note' },
            { _uid: 'default-cta', component: 'q4s_cta' }
        ];
        // Nothing until the CMS query has answered, then one body for good. This
        // used to render DEFAULT_HOME_BODY first and swap to the CMS body when it
        // arrived -- a different hero component with a different key, so the
        // SearchBar inside it was destroyed and rebuilt and whatever the visitor
        // had already typed was wiped. On a slow answer that is seconds of typing.
        // An error also ends `loading`, so a failed query still gets the default.
        const body = computed(() => {
            if (loading.value && !result.value)
                return [];
            const cmsBody = story.value?.content?.body;
            return cmsBody && cmsBody.length ? cmsBody : DEFAULT_HOME_BODY;
        });
        watch(() => result.value, () => {
            if (result.value) {
                storyBase.value = JSON.parse(result.value.get_home_page_cms_content.story).story;
            }
        }, { immediate: true });
        const { injectStructuredData, ejectStructuredData } = useStructuredData();
        const schemaOrgInfo = computed(() => {
            // https://schema.org
            // https://www.searchenginejournal.com/google-site-names/467892/
            return {
                '@context': 'https://schema.org',
                '@graph': [
                    {
                        '@type': 'Organization',
                        // `name` is the brand, `legalName` the entity. Publishing the
                        // brand as the legal name would simply be false, and the B.V.
                        // has not been renamed.
                        name: 'Quote 4 Space',
                        legalName: 'ASQ Global B.V.',
                        url: `${SITE_URL}${root.$router.currentRoute.path}`,
                        logo: `${SITE_URL}/img/brand/q4s-og.png`,
                        contactPoint: {
                            '@type': 'ContactPoint',
                            telephone: '+31 20 204 4544',
                            contactType: 'Customer service'
                        },
                        sameAs: [
                            'https://www.instagram.com/asq.global/',
                            'https://www.facebook.com/ASQ Global/',
                            'https://linkedin.com/company/asq-global',
                            'https://twitter.com/we_meet_NL'
                        ]
                    },
                    {
                        '@type': 'WebSite',
                        name: 'Quote 4 Space',
                        url: SITE_URL,
                        potentialAction: {
                            '@type': 'SearchAction',
                            target: `${SITE_URL}/search?q={search_term_string}`,
                            'query-input': 'required name=search_term_string'
                        }
                    }
                ]
            };
        });
        const makeAuthRedirect = () => {
            const query = root.$route.query;
            const state = query?.state;
            if (state && typeof state === 'string') {
                query.authPopup = AuthPopups[state];
            }
            globalThis.$router.replace({
                name: globalThis.$i18n.locale !== 'nl'
                    ? `en-${ROUTE_LIST_NAMES.MAIN}`
                    : ROUTE_LIST_NAMES.MAIN,
                query: {
                    ...query,
                    token: null,
                    email_token: null,
                    ['verify-email']: null,
                    ['verify-phone-with-link']: null,
                    redirect_url: null
                }
            });
        };
        /**
         * Watch for locale change and refetch the home page content
         */
        watch(() => globalThis.$i18n.locale, () => {
            refetch();
        });
        /**
         * Update structured data to the page when FAQ is loaded
         * @param structuredData
         */
        const injectData = (structuredData) => {
            const data = schemaOrgInfo.value;
            data['@graph'].push(structuredData);
            injectStructuredData({
                '@context': 'https://schema.org',
                ...data
            });
        };
        const faqQuestions = computed(() => [
            {
                question: root.$t('home.faq.question_1.title'),
                answer: root.$t('home.faq.question_1.answer')
            },
            {
                question: root.$t('home.faq.question_2.title'),
                answer: root.$t('home.faq.question_2.answer')
            },
            {
                question: root.$t('home.faq.question_3.title'),
                answer: root.$t('home.faq.question_3.answer')
            },
            {
                question: root.$t('home.faq.question_4.title'),
                answer: root.$t('home.faq.question_4.answer')
            }
        ]);
        if (window.location.search.includes('_storyblok')) {
            // load the bridge only inside of Storyblok Editor
            useStoryblokBridge(story);
            instance.$on('storyPublished', refetch);
        }
        onMounted(() => {
            injectStructuredData(schemaOrgInfo.value);
            makeAuthRedirect();
        });
        onUnmounted(() => {
            ejectStructuredData();
        });
        return {
            story,
            body,
            faqQuestions,
            injectData
        };
    }
});
